// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module EventTable
 * @package @geenius/analytics/react
 * @description Renders the React recent-events table for analytics dashboards.
 * The component formats event metadata into a compact grid and includes loading
 * skeletons plus an empty state for low-traffic views.
 */

import type { AnalyticsEvent, DeviceInfo, EventType } from "@geenius/analytics";
import {
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@geenius/ui/react";
import type React from "react";
import { Fragment, useMemo, useState } from "react";
import { useAnalyticsCopy } from "../lib/analyticsCopy";

type EventTableSortKey = "name" | "type" | "url" | "device" | "timestamp";
type EventTableSortDirection = "asc" | "desc";

const EVENT_TYPE_TRANSLATION_KEYS: Readonly<Record<EventType, string>> = {
  page_view: "analytics.events.types.pageView",
  click: "analytics.events.types.click",
  form_submit: "analytics.events.types.formSubmit",
  custom: "analytics.events.types.custom",
};

const DEVICE_ICONS: Readonly<Record<DeviceInfo["device"], string>> = {
  desktop: "💻",
  tablet: "📟",
  mobile: "📱",
};

interface EventTableSortState {
  key: EventTableSortKey;
  direction: EventTableSortDirection;
}

function formatEventPathname(url: string): string {
  try {
    return new URL(url, "https://geenius.dev").pathname;
  } catch {
    return url;
  }
}

function stringifyEventProperties(properties: Record<string, unknown>): string {
  try {
    return JSON.stringify(properties, null, 2);
  } catch {
    return String(properties);
  }
}

function getErrorMessage(error: Error | string): string {
  return typeof error === "string" ? error : error.message;
}

function toDateInputValue(timestamp?: number): string {
  if (!timestamp) return "";
  return new Date(timestamp).toISOString().slice(0, 10);
}

function fromDateInputValue(
  value: string,
  boundary: "start" | "end",
): number | undefined {
  if (!value) return undefined;
  const date = new Date(
    `${value}T${boundary === "start" ? "00:00:00" : "23:59:59"}`,
  );
  return Number.isNaN(date.getTime()) ? undefined : date.getTime();
}

function includesText(value: string | undefined, query: string): boolean {
  return value?.toLowerCase().includes(query) ?? false;
}

function eventMatchesFilters(
  event: AnalyticsEvent,
  filters?: EventTableFilters,
): boolean {
  if (!filters) return true;
  if (filters.types?.length && !filters.types.includes(event.type))
    return false;
  if (filters.startTime && event.timestamp < filters.startTime) return false;
  if (filters.endTime && event.timestamp > filters.endTime) return false;
  if (filters.device && event.device.device !== filters.device) return false;

  const urlQuery = filters.url?.trim().toLowerCase();
  if (urlQuery && !includesText(event.url, urlQuery)) return false;

  const userQuery = filters.userId?.trim().toLowerCase();
  if (userQuery && !includesText(event.userId, userQuery)) return false;

  const sessionQuery = filters.sessionId?.trim().toLowerCase();
  if (sessionQuery && !includesText(event.sessionId, sessionQuery))
    return false;

  const searchQuery = filters.search?.trim().toLowerCase();
  if (searchQuery) {
    const searchable = [
      event.name,
      event.type,
      event.url,
      event.userId,
      event.sessionId,
      event.device.browser,
      event.device.os,
      stringifyEventProperties(event.properties ?? {}),
    ].join(" ");

    if (!searchable.toLowerCase().includes(searchQuery)) return false;
  }

  return true;
}

function mergeFilters(
  filters: EventTableFilters | undefined,
  patch: Partial<EventTableFilters>,
): EventTableFilters {
  return { ...(filters ?? {}), ...patch };
}

function getSortValue(
  event: AnalyticsEvent,
  key: EventTableSortKey,
): string | number {
  if (key === "device") return event.device.device;
  if (key === "timestamp") return event.timestamp;
  return event[key];
}

function sortEvents(
  events: AnalyticsEvent[],
  sort: EventTableSortState | null,
): AnalyticsEvent[] {
  if (!sort) return events;

  const direction = sort.direction === "asc" ? 1 : -1;
  return [...events].sort((left, right) => {
    const leftValue = getSortValue(left, sort.key);
    const rightValue = getSortValue(right, sort.key);

    if (typeof leftValue === "number" && typeof rightValue === "number") {
      return (leftValue - rightValue) * direction;
    }

    return String(leftValue).localeCompare(String(rightValue)) * direction;
  });
}

function nextSortState(
  current: EventTableSortState | null,
  key: EventTableSortKey,
): EventTableSortState | null {
  if (current?.key !== key) {
    return { key, direction: key === "timestamp" ? "desc" : "asc" };
  }

  if (current.direction === "asc") {
    return { key, direction: "desc" };
  }

  return null;
}

function getAriaSort(
  sort: EventTableSortState | null,
  key: EventTableSortKey,
): React.AriaAttributes["aria-sort"] {
  if (sort?.key !== key) return "none";
  return sort.direction === "asc" ? "ascending" : "descending";
}

export interface EventTableFilters {
  types?: EventType[];
  startTime?: number;
  endTime?: number;
  url?: string;
  userId?: string;
  sessionId?: string;
  device?: DeviceInfo["device"];
  search?: string;
}

/**
 * Props for rendering the React analytics event table.
 *
 * @property events Event rows to display in the table.
 * @property filters Controlled filter values applied before row limiting.
 * @property onFiltersChange Called when a rendered filter control changes.
 * @property showFilters Forces filter controls to render even without a change handler.
 * @property isLoading Whether the table should render loading skeleton rows.
 * @property error Optional table-level error message or object.
 * @property onRetry Optional retry handler rendered when an error is present.
 * @property maxRows Maximum number of event rows to render.
 * @property className Optional class name merged into the wrapper.
 */
export interface EventTableProps {
  events: AnalyticsEvent[];
  filters?: EventTableFilters;
  onFiltersChange?: (filters: EventTableFilters) => void;
  showFilters?: boolean;
  isLoading?: boolean;
  error?: Error | string | null;
  onRetry?: () => void;
  maxRows?: number;
  className?: string;
}

/**
 * Renders the React analytics event table.
 *
 * @param props Event rows, loading state, and row limit for the table.
 * @returns An event table element with loading and empty states.
 */
export function EventTable({
  events,
  filters,
  onFiltersChange,
  showFilters,
  isLoading,
  error,
  onRetry,
  maxRows = 10,
  className,
}: EventTableProps): React.ReactElement {
  const copy = useAnalyticsCopy();
  const [localFilters, setLocalFilters] = useState<EventTableFilters>({});
  const activeFilters = filters ?? localFilters;
  const controlsVisible = showFilters ?? true;
  const [sort, setSort] = useState<EventTableSortState | null>(null);
  const [expandedEventIds, setExpandedEventIds] = useState<Set<string>>(
    () => new Set(),
  );
  const displayed = useMemo(() => {
    const filtered = events.filter((event) =>
      eventMatchesFilters(event, activeFilters),
    );
    return sortEvents(filtered, sort).slice(0, maxRows);
  }, [events, activeFilters, maxRows, sort]);
  const updateFilters = (patch: Partial<EventTableFilters>): void => {
    const next = mergeFilters(activeFilters, patch);
    if (filters === undefined) {
      setLocalFilters(next);
    }
    onFiltersChange?.(next);
  };
  const toggleEventProperties = (eventId: string): void => {
    setExpandedEventIds((current) => {
      const next = new Set(current);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };
  const renderSortableHeader = (
    key: EventTableSortKey,
    label: string,
    className?: string,
  ): React.ReactElement => {
    const active = sort?.key === key;
    const directionLabel =
      active && sort.direction === "asc" ? "ascending" : "descending";

    return (
      <TableHead className={className} aria-sort={getAriaSort(sort, key)}>
        <button
          className="inline-flex items-center gap-2 text-left text-xs font-medium uppercase tracking-normal text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          type="button"
          onClick={() => setSort((current) => nextSortState(current, key))}
        >
          <span>{label}</span>
          <span className="sr-only">
            {active
              ? `Sorted ${directionLabel}; activate to change sorting`
              : `Sort by ${label}`}
          </span>
          <span className="text-[0.65rem]" aria-hidden="true">
            {active ? directionLabel : "sort"}
          </span>
        </button>
      </TableHead>
    );
  };

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="pt-6">
          <div className="grid gap-3" role="alert">
            <span className="text-sm font-medium text-foreground">
              {copy.t("analytics.dashboard.recentEvents")}
            </span>
            <span className="text-sm text-muted-foreground">
              {getErrorMessage(error)}
            </span>
            {onRetry && (
              <Button
                className="w-fit"
                type="button"
                variant="secondary"
                onClick={onRetry}
              >
                Retry
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="grid gap-3 pt-6" data-analytics-table-loading>
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton
              key={index}
              className="h-10 w-full"
              data-analytics-row-skeleton
            />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className} data-analytics-table>
      <CardContent className="pt-6">
        {controlsVisible && (
          <div
            className="mb-4 grid gap-3 rounded-md border border-border bg-muted/30 p-3 sm:grid-cols-2 xl:grid-cols-6"
            data-analytics-table-filters
          >
            <label className="grid gap-1 text-xs font-medium text-muted-foreground">
              <span>{copy.t("analytics.events.filters.search")}</span>
              <input
                className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                value={activeFilters.search ?? ""}
                onChange={(event) =>
                  updateFilters({ search: event.currentTarget.value })
                }
                placeholder={copy.t(
                  "analytics.events.filters.searchPlaceholder",
                )}
              />
            </label>
            <label className="grid gap-1 text-xs font-medium text-muted-foreground">
              <span>{copy.t("analytics.events.filters.type")}</span>
              <select
                className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                value={activeFilters.types?.[0] ?? ""}
                onChange={(event) =>
                  updateFilters({
                    types: event.currentTarget.value
                      ? [event.currentTarget.value as EventType]
                      : undefined,
                  })
                }
              >
                <option value="">
                  {copy.t("analytics.events.filters.allTypes")}
                </option>
                {(["page_view", "click", "form_submit", "custom"] as const).map(
                  (type) => (
                    <option key={type} value={type}>
                      {copy.t(EVENT_TYPE_TRANSLATION_KEYS[type])}
                    </option>
                  ),
                )}
              </select>
            </label>
            <label className="grid gap-1 text-xs font-medium text-muted-foreground">
              <span>{copy.t("analytics.events.filters.url")}</span>
              <input
                className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                value={activeFilters.url ?? ""}
                onChange={(event) =>
                  updateFilters({ url: event.currentTarget.value })
                }
                placeholder="/pricing"
              />
            </label>
            <label className="grid gap-1 text-xs font-medium text-muted-foreground">
              <span>{copy.t("analytics.events.filters.user")}</span>
              <input
                className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                value={activeFilters.userId ?? ""}
                onChange={(event) =>
                  updateFilters({ userId: event.currentTarget.value })
                }
                placeholder="user_"
              />
            </label>
            <label className="grid gap-1 text-xs font-medium text-muted-foreground">
              <span>{copy.t("analytics.events.filters.session")}</span>
              <input
                className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                value={activeFilters.sessionId ?? ""}
                onChange={(event) =>
                  updateFilters({ sessionId: event.currentTarget.value })
                }
                placeholder="ses_"
              />
            </label>
            <label className="grid gap-1 text-xs font-medium text-muted-foreground">
              <span>{copy.t("analytics.events.filters.device")}</span>
              <select
                className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                value={activeFilters.device ?? ""}
                onChange={(event) =>
                  updateFilters({
                    device: event.currentTarget.value
                      ? (event.currentTarget.value as DeviceInfo["device"])
                      : undefined,
                  })
                }
              >
                <option value="">
                  {copy.t("analytics.events.filters.allDevices")}
                </option>
                {(["desktop", "tablet", "mobile"] as const).map((device) => (
                  <option key={device} value={device}>
                    {copy.deviceLabel(device)}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-1 text-xs font-medium text-muted-foreground">
              <span>{copy.t("analytics.events.filters.from")}</span>
              <input
                className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                type="date"
                value={toDateInputValue(activeFilters.startTime)}
                onChange={(event) =>
                  updateFilters({
                    startTime: fromDateInputValue(
                      event.currentTarget.value,
                      "start",
                    ),
                  })
                }
              />
            </label>
            <label className="grid gap-1 text-xs font-medium text-muted-foreground">
              <span>{copy.t("analytics.events.filters.to")}</span>
              <input
                className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                type="date"
                value={toDateInputValue(activeFilters.endTime)}
                onChange={(event) =>
                  updateFilters({
                    endTime: fromDateInputValue(
                      event.currentTarget.value,
                      "end",
                    ),
                  })
                }
              />
            </label>
          </div>
        )}
        {displayed.length === 0 ? (
          <EmptyState
            bordered={false}
            compact
            title={copy.t("analytics.events.empty")}
          />
        ) : (
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                {renderSortableHeader(
                  "name",
                  copy.t("analytics.events.columns.event"),
                )}
                {renderSortableHeader(
                  "type",
                  copy.t("analytics.events.columns.type"),
                )}
                {renderSortableHeader(
                  "url",
                  copy.t("analytics.events.columns.url"),
                )}
                {renderSortableHeader(
                  "device",
                  copy.t("analytics.events.columns.device"),
                )}
                {renderSortableHeader(
                  "timestamp",
                  copy.t("analytics.events.columns.time"),
                  "text-right",
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayed.map((event) => {
                const hasProperties =
                  Object.keys(event.properties ?? {}).length > 0;
                const isExpanded = expandedEventIds.has(event.id);
                const propertiesId = `gn-analytics-event-properties-${event.id}`;

                return (
                  <Fragment key={event.id}>
                    <TableRow data-analytics-table-row>
                      <TableCell>
                        <div className="flex flex-col gap-2">
                          <span
                            className="font-medium text-foreground"
                            data-analytics-cell-name
                          >
                            {event.name}
                          </span>
                          {hasProperties ? (
                            <button
                              className="w-fit text-xs font-medium text-primary underline-offset-4 transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              type="button"
                              aria-controls={propertiesId}
                              aria-expanded={isExpanded}
                              aria-label={`${isExpanded ? "Hide" : "Show"} properties for ${event.name}`}
                              onClick={() => toggleEventProperties(event.id)}
                            >
                              {isExpanded
                                ? "Hide properties"
                                : "Show properties"}
                            </button>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          size="sm"
                          variant="secondary"
                          data-analytics-cell-type
                          data-type={event.type}
                        >
                          {copy.t(EVENT_TYPE_TRANSLATION_KEYS[event.type])}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span
                          className="text-sm text-muted-foreground"
                          data-analytics-cell-url
                          title={event.url}
                        >
                          {formatEventPathname(event.url)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span
                          className="text-sm text-muted-foreground"
                          data-analytics-cell-device
                          data-device={event.device.device}
                        >
                          {DEVICE_ICONS[event.device.device]}{" "}
                          {copy.deviceLabel(event.device.device)} ·{" "}
                          {event.device.browser}
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">
                        <span data-analytics-cell-time>
                          {copy.formatTime(event.timestamp)}
                        </span>
                      </TableCell>
                    </TableRow>
                    {hasProperties && isExpanded ? (
                      <TableRow
                        id={propertiesId}
                        data-analytics-table-properties
                      >
                        <TableCell colSpan={5}>
                          <pre className="max-h-72 overflow-auto rounded-md border border-border bg-muted/40 p-3 text-xs text-foreground">
                            {stringifyEventProperties(event.properties ?? {})}
                          </pre>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
